import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const fmtRM = (n) => `RM${Number(n || 0).toLocaleString("en-US")}`;

function ModalShell({ onClose, testid, eyebrow, title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="card-lux relative w-full max-w-4xl p-6 sm:p-8 max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="eyebrow-label mb-2">{eyebrow}</p>
            <h3 className="font-heading text-lg font-semibold text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-white transition-colors" data-testid={`${testid}-close`}>
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SummaryChip({ label, value, testid, accent }) {
  return (
    <div className="rounded-xl border border-[#222530] bg-[#0B0C10]/60 px-4 py-3" data-testid={testid}>
      <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">{label}</p>
      <p className={`mt-1 font-mono text-lg font-semibold ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

export function RankCostModal({ onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);

  const loadData = () =>
    api.get("/admin/ranks/costs").then((res) => setData(res.data)).catch((e) => setError(formatApiError(e)));

  useEffect(() => { loadData(); }, []);

  const handleRemove = async (m) => {
    if (!window.confirm(
      `Remove this unpaid Rank Cost of ${fmtRM(m.cost)} for ${m.member_name} (${m.member_uid || m.member_id})?\n\n` +
      `Only this cost is removed. The member's progress and rank are NOT changed.`
    )) return;
    setRemovingId(m.accrual_id);
    try {
      await api.post(`/admin/ranks/costs/${m.accrual_id}/remove`);
      toast.success(`Removed ${fmtRM(m.cost)} cost for ${m.member_name}`);
      await loadData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <ModalShell onClose={onClose} testid="rank-cost-modal" eyebrow="Admin Only" title="Rank Cost — Achieved Live Rank">
      {error && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="rank-cost-error">{error}</p>}
      {!data && !error && <p className="text-sm text-[#64748B]" data-testid="rank-cost-loading">Loading cost data...</p>}
      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryChip label="Outstanding Members" value={data.summary.member_count} testid="rank-cost-member-count" />
            <SummaryChip label="Cost Wallet (Outstanding)" value={fmtRM(data.summary.total_cost)} testid="rank-cost-total" accent="text-[#D4AF37]" />
            <SummaryChip label="Resolved via Claims" value={fmtRM(data.summary.total_resolved ?? 0)} testid="rank-cost-resolved" accent="text-emerald-400" />
            <SummaryChip label="Currency" value={data.currency} testid="rank-cost-currency" />
          </div>
          <p className="text-[10px] text-[#64748B] font-mono tracking-wide" data-testid="rank-cost-note">
            The wallet accrues when a member achieves a rank and is reduced when they claim — never recalculated from live ranks.
          </p>

          <div>
            <p className="text-xs font-medium text-[#94A3B8] tracking-wide mb-2">Outstanding Cost by Member (Achieved, Unclaimed)</p>
            <div className="rounded-xl border border-[#222530] overflow-hidden">
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm" data-testid="rank-cost-members-table">
                  <thead className="sticky top-0 bg-[#0B0C10]">
                    <tr className="border-b border-[#222530] text-left">
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Member</th>
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B]">UID</th>
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Achieved Rank</th>
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B] text-right">Cost</th>
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B] text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222530]">
                    {data.members.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-[#64748B]" data-testid="rank-cost-members-empty">No outstanding cost — every achieved rank has been claimed.</td></tr>
                    ) : (
                      data.members.map((m) => (
                        <tr key={m.accrual_id || m.member_id} className="hover:bg-[#0B0C10]/60" data-testid={`rank-cost-member-${m.member_uid || m.member_id}`}>
                          <td className="px-4 py-2.5 text-white">{m.member_name}</td>
                          <td className="px-4 py-2.5 font-mono text-[#94A3B8]">{m.member_uid || "—"}</td>
                          <td className="px-4 py-2.5 text-white">
                            {m.achieved_rank}
                            <span className="ml-2 text-[10px] font-mono text-[#64748B]">L{m.achieved_level}</span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-[#D4AF37] text-right">{fmtRM(m.cost)}</td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={() => handleRemove(m)}
                              disabled={removingId === m.accrual_id}
                              data-testid={`rank-cost-remove-${m.member_uid || m.member_id}`}
                              className="text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border border-[#222530] text-[#94A3B8] hover:border-red-400/50 hover:text-red-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {removingId === m.accrual_id ? "Removing…" : "Remove Cost"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-[#94A3B8] tracking-wide mb-2">Official 36-Tier Cost Table (Fixed)</p>
            <div className="rounded-xl border border-[#222530] overflow-hidden">
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm" data-testid="rank-cost-table">
                  <thead className="sticky top-0 bg-[#0B0C10]">
                    <tr className="border-b border-[#222530] text-left">
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Level</th>
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Rank</th>
                      <th className="px-4 py-3 text-xs font-medium text-[#64748B] text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222530]">
                    {data.table.map((t) => (
                      <tr key={t.level} className="hover:bg-[#0B0C10]/60" data-testid={`rank-cost-tier-${t.level}`}>
                        <td className="px-4 py-2 font-mono text-[#D4AF37]">{t.level}</td>
                        <td className="px-4 py-2 text-white">{t.name}</td>
                        <td className="px-4 py-2 font-mono text-white text-right">{fmtRM(t.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

export function RankProfitModal({ onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/ranks/profits").then((res) => setData(res.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  return (
    <ModalShell onClose={onClose} testid="rank-profit-modal" eyebrow="Admin Only" title="Profit / Spread — Achieved Cost − Claimed Cost">
      {error && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="rank-profit-error">{error}</p>}
      {!data && !error && <p className="text-sm text-[#64748B]" data-testid="rank-profit-loading">Loading profit data...</p>}
      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryChip label="Claims Counted" value={data.summary.claim_count} testid="rank-profit-claim-count" />
            <SummaryChip label="Achieved Cost" value={fmtRM(data.summary.total_achieved_cost)} testid="rank-profit-achieved-total" />
            <SummaryChip label="Claimed Cost" value={fmtRM(data.summary.total_claimed_cost)} testid="rank-profit-claimed-total" />
            <SummaryChip label="Total Profit" value={fmtRM(data.summary.total_profit)} testid="rank-profit-total" accent="text-emerald-400" />
          </div>
          <p className="text-[10px] text-[#64748B] font-mono tracking-wide" data-testid="rank-profit-note">
            Rejected claims are excluded. Achieved rank is the member's live rank at the moment of claim.
          </p>
          <div className="rounded-xl border border-[#222530] overflow-hidden">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm" data-testid="rank-profit-table">
                <thead className="sticky top-0 bg-[#0B0C10]">
                  <tr className="border-b border-[#222530] text-left">
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Member</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Reward</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Achieved (Cost)</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Claimed (Cost)</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B] text-right">Profit</th>
                    <th className="px-4 py-3 text-xs font-medium text-[#64748B]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#222530]">
                  {data.items.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-[#64748B]" data-testid="rank-profit-empty">No claims yet.</td></tr>
                  ) : (
                    data.items.map((it) => (
                      <tr key={it.id} className="hover:bg-[#0B0C10]/60" data-testid={`rank-profit-row-${it.id}`}>
                        <td className="px-4 py-2.5">
                          <p className="text-white">{it.member_name}</p>
                          <p className="text-[10px] font-mono text-[#64748B]">{it.member_uid || "—"}</p>
                        </td>
                        <td className="px-4 py-2.5 text-[#94A3B8] max-w-[180px] truncate">{it.reward_name}</td>
                        <td className="px-4 py-2.5 text-white">
                          {it.achieved_rank}
                          <span className="block text-[10px] font-mono text-[#D4AF37]">{fmtRM(it.achieved_cost)}</span>
                        </td>
                        <td className="px-4 py-2.5 text-white">
                          {it.claimed_rank}
                          <span className="block text-[10px] font-mono text-[#94A3B8]">{fmtRM(it.claimed_cost)}</span>
                        </td>
                        <td className={`px-4 py-2.5 font-mono text-right ${it.profit >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`rank-profit-value-${it.id}`}>
                          {fmtRM(it.profit)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-[10px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border border-[#222530] text-[#94A3B8]">{it.status}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
